import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { TaxWithUsage } from '../../types/tax.type';
import { UpdateTaxRequestDTO } from '../../types/dtos/update-tax.dto';
import { formatTaxUsage } from '../../utils/tax-usage.util';
import { getCommaTaxAmountCanonicalForm, normalizeTaxAmount } from '../../utils/tax-amount.util';
import { INVALID_TAX_AMOUNT_HELPER_TEXT } from '../../constants/tax-amount.constants';

const TAX_NAME_INPUT_ID = 'edit-tax-name';
const TAX_AMOUNT_INPUT_ID = 'edit-tax-amount';

type Props = {
  open: boolean;
  tax: TaxWithUsage | null;
  isSubmitting: boolean;
  onSubmit: (data: UpdateTaxRequestDTO) => Promise<void> | void;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  paper: {
    minWidth: 400,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  normalizeButton: {
    alignSelf: 'flex-start',
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

function getAmountHelperText(usageCount: number, isAmountInvalid: boolean): string | undefined {
  if (usageCount > 0) return `Utilisée par ${formatTaxUsage(usageCount)} : le taux ne peut pas changer`;
  if (isAmountInvalid) return INVALID_TAX_AMOUNT_HELPER_TEXT;
  return undefined;
}

export function TaxDialog({ open, tax, isSubmitting, onSubmit, onClose }: Props) {
  const classes = useStyles();

  const [name, setName] = useState(tax?.name ?? '');
  const [amount, setAmount] = useState(tax?.amount ?? '');

  if (!tax) return null;

  const isRateFrozen = tax.usageCount > 0;
  const commaAmountCanonicalForm = getCommaTaxAmountCanonicalForm(tax.amount);

  const trimmedName = name.trim();
  const trimmedAmount = amount.trim();
  const normalizedAmount = normalizeTaxAmount(trimmedAmount);
  const isAmountEdited = !isRateFrozen && trimmedAmount !== tax.amount && normalizedAmount !== tax.amount;
  const isAmountInvalid = isAmountEdited && normalizedAmount === null;

  const changes: UpdateTaxRequestDTO = {};
  if (trimmedName !== tax.name) changes.name = trimmedName;
  if (isAmountEdited && normalizedAmount !== null) changes.amount = normalizedAmount;

  const hasChanges = Object.keys(changes).length > 0;
  const isValid = trimmedName !== '' && !isAmountInvalid;
  const canSubmit = hasChanges && isValid && !isSubmitting;

  const isNormalizeDisabled = isSubmitting || trimmedName === '';

  const amountHelperText = getAmountHelperText(tax.usageCount, isAmountInvalid);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(changes);
  };

  const handleNormalize = () => {
    if (isNormalizeDisabled || commaAmountCanonicalForm === null) return;
    onSubmit({ ...changes, amount: commaAmountCanonicalForm });
  };

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.paper }} disableEscapeKeyDown={isSubmitting}>
      <DialogTitle>Modifier la taxe</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            id={TAX_NAME_INPUT_ID}
            label="Nom"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            id={TAX_AMOUNT_INPUT_ID}
            label="Taux (%)"
            value={amount}
            disabled={isRateFrozen}
            error={isAmountInvalid}
            helperText={amountHelperText}
            onChange={(event) => setAmount(event.target.value)}
          />
          {commaAmountCanonicalForm !== null && (
            <Button
              size="small"
              variant="outlined"
              className={classes.normalizeButton}
              disabled={isNormalizeDisabled}
              onClick={handleNormalize}
            >
              {`Normaliser en ${commaAmountCanonicalForm}`}
            </Button>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.saveButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
